import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Badge } from '@/components/ui/Badge';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollableTable } from '@/components/ui/ScrollableTable';
import { SearchInput } from '@/components/ui/SearchInput';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table';
import { cn } from '@/lib/utils';

import type { TeamUsageMember } from '../../team.types';

const COLUMNS = [
  { key: 'messages', label: 'Messages' },
  { key: 'integration_actions', label: 'Integrations' },
  { key: 'routine_runs', label: 'Routines' },
  { key: 'meetings', label: 'Meetings' },
] as const;

type SortColumn = (typeof COLUMNS)[number]['key'];
type SortDirection = 'asc' | 'desc';
type SortState = { column: SortColumn; direction: SortDirection };
type StatusFilter = 'all' | 'active' | 'inactive';

function selectMembers(
  members: TeamUsageMember[],
  search: string,
  status: StatusFilter,
  sort: SortState
): TeamUsageMember[] {
  const query = search.trim().toLowerCase();
  const sign = sort.direction === 'desc' ? -1 : 1;
  return members
    .filter(
      (member) =>
        (!query ||
          (member.name ?? '').toLowerCase().includes(query) ||
          member.email.toLowerCase().includes(query)) &&
        (status === 'all' || member.is_active === (status === 'active'))
    )
    .sort((a, b) => sign * (a[sort.column] - b[sort.column]));
}

export function MembersByUsageTable({
  members,
  onSelectMember,
  className,
}: {
  members: TeamUsageMember[];
  onSelectMember: (membershipId: string) => void;
  className?: string;
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<SortState>({
    column: 'messages',
    direction: 'desc',
  });

  const activeCount = members.filter((member) => member.is_active).length;
  const rows = selectMembers(members, search, status, sort);
  const toggleSort = (column: SortColumn) =>
    setSort((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
    }));

  return (
    <YStack className={cn('gap-3', className)}>
      <YStack className="gap-0.5">
        <span className="text-base font-medium text-foreground">
          Members by usage
        </span>
        <span className="text-sm text-foreground-muted">
          Click a member for a detailed breakdown
        </span>
      </YStack>
      <XStack className="items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search members..."
          aria-label="Search members"
          className="w-64 flex-none"
        />
        <SegmentedControl.Root
          value={status}
          onValueChange={(value) => setStatus(value as StatusFilter)}
        >
          <SegmentedControl.List className="h-9">
            <SegmentedControl.Trigger value="all">
              All ({members.length})
            </SegmentedControl.Trigger>
            <SegmentedControl.Trigger value="active">
              Active ({activeCount})
            </SegmentedControl.Trigger>
            <SegmentedControl.Trigger value="inactive">
              Inactive ({members.length - activeCount})
            </SegmentedControl.Trigger>
          </SegmentedControl.List>
        </SegmentedControl.Root>
      </XStack>
      <ScrollableTable
        header={
          <TableRow className="border-b border-border">
            <TableHead className="px-4">Member</TableHead>
            {COLUMNS.map(({ key, label }) => (
              <SortableHead
                key={key}
                label={label}
                active={sort.column === key}
                direction={sort.direction}
                onSort={() => toggleSort(key)}
              />
            ))}
            <TableHead className="w-10" />
          </TableRow>
        }
      >
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={COLUMNS.length + 2}
                className="px-4 py-6 text-center text-foreground-muted"
              >
                No members match your filters.
              </TableCell>
            </TableRow>
          )}
          {rows.map((member) => (
            <MemberUsageRow
              key={member.membership_id}
              member={member}
              onSelect={onSelectMember}
            />
          ))}
        </TableBody>
      </ScrollableTable>
    </YStack>
  );
}

function SortableHead({
  label,
  active,
  direction,
  onSort,
}: {
  label: string;
  active: boolean;
  direction: SortDirection;
  onSort: () => void;
}) {
  const DirectionIcon =
    direction === 'desc' ? Icons.chevronDown : Icons.chevronUp;
  return (
    <TableHead
      className="px-4 text-right"
      aria-sort={
        active ? (direction === 'desc' ? 'descending' : 'ascending') : undefined
      }
    >
      <button
        type="button"
        onClick={onSort}
        className={cn(
          'focus-ring inline-flex items-center gap-1 hover:text-foreground',
          active && 'text-foreground'
        )}
      >
        {label}
        {active && <DirectionIcon className="size-3" />}
      </button>
    </TableHead>
  );
}

function MemberUsageRow({
  member,
  onSelect,
}: {
  member: TeamUsageMember;
  onSelect: (membershipId: string) => void;
}) {
  return (
    <TableRow
      tabIndex={0}
      aria-label={`View usage for ${member.name ?? member.email}`}
      className={cn(
        'group cursor-pointer bg-surface hover:bg-surface-hover',
        'focus-visible:bg-surface-hover focus-visible:outline focus-visible:outline-2',
        'focus-visible:-outline-offset-2 focus-visible:outline-ring'
      )}
      onClick={() => onSelect(member.membership_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(member.membership_id);
        }
      }}
    >
      <TableCell className="px-4 py-3">
        <YStack className="gap-0">
          <XStack className="items-center gap-2">
            <span className="font-medium text-foreground">
              {member.name ?? member.email}
            </span>
            {!member.is_active && (
              <Badge variant="secondary" className="text-foreground-muted">
                Inactive
              </Badge>
            )}
          </XStack>
          <span className="text-xs text-foreground-muted">{member.email}</span>
        </YStack>
      </TableCell>
      {COLUMNS.map(({ key }) => (
        <CountCell key={key} value={member[key]} />
      ))}
      <TableCell className="pr-4 text-right">
        <Icons.chevronRight className="ml-auto size-4 text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
      </TableCell>
    </TableRow>
  );
}

function CountCell({ value }: { value: number }) {
  return (
    <TableCell
      className={cn(
        'px-4 text-right tabular-nums',
        value === 0 && 'text-foreground-muted'
      )}
    >
      {value.toLocaleString()}
    </TableCell>
  );
}
